import React, { useState, useEffect, useRef } from 'react';
import { Link, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  LayoutDashboard, Video, CreditCard, User, ChevronRight, Clock,
  CheckCircle, AlertTriangle, Edit3, Lock, Eye, EyeOff, Save, X, Play
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useUI } from '../context/UIContext';
import { api } from '../utils/api';
import Pagination from '../components/Pagination';

const userNav = [
  { to: '/dashboard', label: 'Overview', icon: LayoutDashboard },
  { to: '/dashboard/my-videos', label: 'My Videos', icon: Video },
  { to: '/dashboard/subscription', label: 'Subscription', icon: CreditCard },
  { to: '/dashboard/profile', label: 'Profile', icon: User },
];

function VideoModal({ video, onClose }: { video: any; onClose: () => void }) {
  const [yid, setYid] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get(`/videos/${video.id}/play`)
      .then((res: any) => setYid(res.youtube_id || ''))
      .catch(() => setError('Unable to load video. Please check your subscription.'))
      .finally(() => setLoading(false));
  }, [video.id]);

  useEffect(() => {
    if (yid && containerRef.current) {
      containerRef.current.requestFullscreen?.().catch((err: any) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    }
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => { });
      }
    };
  }, [yid]);

  if (loading) return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
      <div className="w-10 h-10 rounded-full border-4 border-white border-t-transparent animate-spin" />
    </div>
  );

  if (error || !yid) return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
      <div className="text-white text-center">
        <p className="text-lg font-bold mb-4">{error || 'Video not available.'}</p>
        <button onClick={onClose} className="btn btn-primary px-8">Close</button>
      </div>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl select-none">
      <div ref={containerRef} className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10 group">
        <button onClick={onClose} className="absolute top-4 right-4 z-40 p-2 rounded-full bg-black/50 hover:bg-white/10 text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        <iframe
          src={`https://www.youtube-nocookie.com/embed/${yid}?rel=0&autoplay=1&modestbranding=1&disablekb=1&showinfo=0&iv_load_policy=3&controls=1&origin=${window.location.origin}`}
          title={video.title}
          className="w-full h-full border-0 relative z-10"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />

        <div className="absolute inset-0 z-20 pointer-events-none">
          {/* Top bar overlay to block "Copy link" and title info */}
          <div className="absolute top-0 left-0 right-0 h-24 pointer-events-auto cursor-default" />

          {/* Bottom right overlay to block YouTube logo and "Watch on YouTube" button */}
          <div className="absolute bottom-0 right-0 w-48 h-20 pointer-events-auto cursor-default" />

          {/* Bottom left overlay to prevent access to branding if it appears there */}
          <div className="absolute bottom-0 left-0 w-16 h-16 pointer-events-auto cursor-default" />
        </div>
      </div>
    </motion.div>
  );
}

function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ videos: 0, active: 0, expiring: 0 });
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/dashboard/user/summary');
        const d = res.data || {};
        setStats({ videos: d.videos || 0, active: d.active || 0, expiring: d.expiring || 0 });
      } catch (error: any) {
        // Don't show unauthorized errors for users without subscriptions
        if (error.message?.includes('unauthorized') || error.message?.includes('401')) {
          // Set default stats for users without subscriptions
          setStats({ videos: 0, active: 0, expiring: 0 });
        } else {
          console.error('Failed to fetch user stats:', error);
          setStats({ videos: 0, active: 0, expiring: 0 });
        }
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-[var(--color-text)] tracking-tight mb-1">
            Dashboard Overview
          </h1>
          <p className="text-xs sm:text-sm text-[var(--color-muted)] font-medium">Welcome back, <span className="text-[var(--color-text)]">{user?.name || user?.email?.split('@')[0]}</span>. Here's what's happening today.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
        {[
          { label: 'Purchased Courses', value: stats.videos, icon: Video, color: '#10b981', sub: 'Total active courses' },
          { label: 'Active Access', value: stats.active, icon: CheckCircle, color: '#3b82f6', sub: 'Subscription status: Active' },
          { label: 'Expiring Soon', value: stats.expiring, icon: AlertTriangle, color: '#f59e0b', sub: 'Needs renewal within 7 days' },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="group bg-[var(--color-bg)] rounded-2xl border border-[var(--glass-border)] p-4 sm:p-6 hover:shadow-xl hover:shadow-[#28503c08] transition-all duration-300 card-hover">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-colors shadow-sm" style={{ background: `${s.color}10` }}>
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: s.color }} />
                </div>
                <div className="text-[9px] sm:text-[10px] font-bold text-[var(--color-muted)] flex items-center gap-1 group-hover:text-[var(--color-accent)] transition-colors">
                  VIEW ALL <ChevronRight className="w-3 h-3" />
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-[var(--color-text)] mb-1 tracking-tight">{s.value}</p>
              <p className="text-xs sm:text-sm font-bold text-[var(--color-text)] mb-1">{s.label}</p>
              <p className="text-[10px] sm:text-[11px] text-[var(--color-muted)] font-medium">{s.sub}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-[var(--color-bg)] rounded-2xl border border-[var(--glass-border)] p-4 sm:p-6 md:p-8">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h2 className="text-base sm:text-lg font-black text-[var(--color-text)] tracking-tight">Quick Actions</h2>
            <p className="text-[10px] sm:text-xs text-[var(--color-muted)] font-medium">Easily navigate to important sections of your portal.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {[
            { label: 'Browse Courses', to: '/services', subtitle: 'Explore new opportunities' },
            { label: 'My Videos', to: '/dashboard/my-videos', subtitle: 'Continue your learning' },
            { label: 'Subscription', to: '/dashboard/subscription', subtitle: 'Manage active plans' },
            { label: 'Support', to: '/support', subtitle: 'Get expert help' },
          ].map(l => (
            <Link key={l.to} to={l.to}
              className="group p-3 sm:p-4 rounded-2xl border border-[var(--glass-border)] hover:border-[var(--color-accent)] hover:bg-[rgba(95,156,76,0.02)] transition-all duration-300 text-left">
              <p className="text-xs sm:text-sm font-bold text-[var(--color-text)] group-hover:text-[var(--color-accent)] transition-colors mb-1">{l.label}</p>
              <p className="text-[9px] sm:text-[10px] text-[var(--color-muted)] font-medium leading-tight">{l.subtitle}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// Reuse MyVideos, Subscription, Profile with slight padding adjustments
function MyVideos() {
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<any>(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  const fetchVideos = (page = 1) => {
    setLoading(true);
    api.get(`/videos/user?page=${page}&limit=6&subscribed=true`)
      .then((res: any) => {
        setVideos(res.data || []);
        setPagination(res.pagination || { page: 1, totalPages: 1, total: 0 });
      })
      .catch((error: any) => {
        // Don't show unauthorized errors for users without subscriptions
        if (error.message?.includes('unauthorized') || error.message?.includes('401')) {
          setVideos([]);
        } else {
          console.error('Failed to fetch videos:', error);
          setVideos([]);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchVideos(1); }, []);

  if (loading) return <div className="py-24 text-center text-sm font-bold text-[var(--color-muted)] animate-pulse">Loading course library...</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-black text-[var(--color-text)] tracking-tight">My Videos</h1>
          <p className="text-xs text-[var(--color-muted)] font-medium mt-1">Access your enrolled professional courses.</p>
        </div>
        <Link to="/services" className="btn btn-primary btn-sm rounded-xl py-2 px-4 text-[11px] w-full sm:w-auto text-center">Enrol New</Link>
      </div>
      {videos.length === 0 ? (
        <div className="bg-[var(--color-bg)] rounded-2xl border border-[var(--glass-border)] p-8 sm:p-12 text-center max-w-lg mx-auto mt-8 sm:mt-12">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[rgba(95,156,76,0.08)] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Video className="w-7 h-7 sm:w-8 sm:h-8 text-[var(--color-accent)]" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-[var(--color-text)] mb-2">No active courses yet</h3>
          <p className="text-xs sm:text-sm text-[var(--color-muted)] mb-6 sm:mb-8 max-w-xs mx-auto">Start your investment education journey by browsing our professional courses.</p>
          <Link to="/services" className="btn btn-primary rounded-xl w-full sm:w-auto px-8 sm:px-10">Explore Our Courses</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {videos.map(v => {
            return (
              <div key={v.id} className="bg-[var(--color-bg)] rounded-2xl border border-[var(--glass-border)] overflow-hidden hover:shadow-lg transition-all duration-300 card-hover cursor-pointer"
                onClick={() => !v.isExpired && setActiveVideo(v)}>
                <div className="aspect-video bg-[var(--glass-bg)] relative group">
                  {v.thumbnail_url ? (
                    <img
                      src={`${import.meta.env.VITE_API_URL || ''}${v.thumbnail_url}`}
                      alt={v.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-black/5 opacity-40">
                      <Video className="w-10 h-10" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-[var(--color-accent)] flex items-center justify-center scale-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] font-black uppercase tracking-widest text-[var(--color-accent)] bg-[rgba(95,156,76,0.1)] px-2 py-0.5 rounded-full">
                      {v.category}
                    </span>
                  </div>
                  <p className="text-sm font-bold text-[var(--color-text)] mb-3 line-clamp-2 min-h-[40px] leading-tight">{v.title}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-[var(--glass-border)]">
                    {v.isExpired ? (
                      <span className="text-[10px] font-black text-red-500">EXPIRED</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-[var(--color-muted)]" />
                        <span className="text-[10px] font-bold text-[var(--color-muted)]">Until {new Date(v.expiryDate).toLocaleDateString()}</span>
                      </div>
                    )}
                    <button className="text-[10px] font-black text-[var(--color-text)] hover:text-[var(--color-accent)] transition-colors">WATCH NOW</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {activeVideo && <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />}
      </AnimatePresence>
    </div>
  );
}

function Subscription() {
  const [subs, setSubs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const res = await api.get('/subscriptions/my');
        setSubs(res.data || []);
      } catch (error: any) {
        // Don't show unauthorized errors for users without subscriptions
        if (error.message?.includes('unauthorized') || error.message?.includes('401')) {
          setSubs([]);
        } else {
          console.error('Failed to fetch subscriptions:', error);
          setSubs([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, []);

  return (
    <div className="p-6 md:p-8 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-black text-[var(--color-text)] tracking-tight">Access & Subscriptions</h1>
          <p className="text-sm text-[var(--color-muted)] font-medium">Manage your learning journey and active plans.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <div className="xl:col-span-1">
          <div className="bg-[var(--color-bg)] rounded-3xl border border-[var(--glass-border)] p-8 text-center sticky top-8">
            <div className="w-16 h-16 bg-[rgba(59,130,246,0.08)] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
              <CreditCard className="w-8 h-8 text-[#3b82f6]" />
            </div>
            <h3 className="text-xl font-black text-[var(--color-text)] mb-3 tracking-tight">Professional Membership</h3>
            <p className="text-sm text-[var(--color-muted)] font-medium mb-8 leading-relaxed">
              Your account is in good standing. You have full access to all purchased course materials.
            </p>

            <div className="bg-[var(--color-bg-2)]/50 rounded-2xl border border-[var(--glass-border)] p-6 mb-8 flex flex-col items-center justify-center">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-[var(--color-accent)]" />
                <span className="text-sm font-bold text-[var(--color-text)]">Status: Active</span>
              </div>
              <p className="text-[11px] font-medium text-[var(--color-muted)]">Valid for lifetime/purchased duration</p>
            </div>

            <Link to="/services" className="btn btn-primary rounded-xl w-full py-4 shadow-lg shadow-[var(--color-accent)]/20 text-[11px] font-black uppercase tracking-widest">
              Expand Your Network
            </Link>
          </div>
        </div>

        <div className="xl:col-span-2 space-y-6">
          <div className="bg-[var(--color-bg)] rounded-3xl border border-[var(--glass-border)] overflow-hidden shadow-sm">
            <div className="p-6 border-b border-[var(--glass-border)] flex items-center justify-between">
              <h3 className="text-sm font-black text-[var(--color-text)] uppercase tracking-wider">Purchase History</h3>
              <span className="text-[10px] font-bold text-[var(--color-muted)] bg-[var(--color-bg-2)] px-2 py-1 rounded-lg">
                {subs.length} {subs.length === 1 ? 'Course' : 'Courses'}
              </span>
            </div>

            <div className="overflow-x-auto">
              {loading ? (
                <div className="py-20 text-center">
                  <div className="w-8 h-8 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-xs font-bold text-[var(--color-muted)]">Fetching your details...</p>
                </div>
              ) : subs.length === 0 ? (
                <div className="py-20 text-center px-6">
                  <div className="w-12 h-12 bg-[var(--color-bg-2)] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Video className="w-6 h-6 text-[var(--color-muted)] opacity-40" />
                  </div>
                  <p className="text-sm font-bold text-[var(--color-text)] mb-1">No courses purchased yet</p>
                  <p className="text-xs text-[var(--color-muted)] mb-6">Explore our library to find your first professional course.</p>
                  <Link to="/services" className="text-[10px] font-black text-[var(--color-accent)] uppercase tracking-widest hover:underline">Browse Courses</Link>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[var(--color-bg-2)]/30">
                      <th className="px-6 py-4 text-[10px] font-black text-[var(--color-muted)] uppercase tracking-widest">Course Name</th>
                      <th className="px-6 py-4 text-[10px] font-black text-[var(--color-muted)] uppercase tracking-widest">Purchase Details</th>
                      <th className="px-6 py-4 text-[10px] font-black text-[var(--color-muted)] uppercase tracking-widest">Validity</th>
                      <th className="px-6 py-4 text-[10px] font-black text-[var(--color-muted)] uppercase tracking-widest text-right">Amount</th>
                      <th className="px-6 py-4 text-[10px] font-black text-[var(--color-muted)] uppercase tracking-widest text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--glass-border)]">
                    {subs.map((s: any) => (
                      <tr key={s.id} className="hover:bg-[var(--color-bg-2)]/20 transition-colors">
                        <td className="px-6 py-5">
                          <p className="text-sm font-bold text-[var(--color-text)] leading-tight mb-1">{s.course_name}</p>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] font-bold text-[var(--color-muted)] uppercase tracking-tighter">ID: SUB-{s.id.toString().padStart(4, '0')}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-[var(--color-muted)] w-12">Bought:</span>
                              <span className="text-[10px] font-black text-[var(--color-text)]">{new Date(s.purchase_date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-[var(--color-muted)] w-12">Expires:</span>
                              <span className="text-[10px] font-black text-[var(--color-text)]">{new Date(s.expiry_date).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-[11px] font-black text-[var(--color-text)]">{s.validity} Days</span>
                          <p className="text-[9px] font-medium text-[var(--color-muted)] uppercase mt-0.5 tracking-tight">Full Course Access</p>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <p className="text-sm font-black text-[var(--color-accent)]">₹{Number(s.amount).toLocaleString()}</p>
                        </td>
                        <td className="px-6 py-5 text-center">
                          {(() => {
                            const now = new Date();
                            const expiry = new Date(s.expiry_date);
                            const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

                            let statusText = s.status;
                            let badgeClass = 'bg-[rgba(95,156,76,0.1)] text-[var(--color-accent)]';

                            if (now > expiry) {
                              statusText = 'Expired';
                              badgeClass = 'bg-red-50 text-red-600';
                            } else if (diffDays <= 5) {
                              statusText = 'Expiry Soon';
                              badgeClass = 'bg-orange-50 text-orange-600';
                            } else if (s.status === 'active') {
                              statusText = 'Active';
                              badgeClass = 'bg-[rgba(95,156,76,0.1)] text-[var(--color-accent)]';
                            }

                            return (
                              <span className={`inline-flex px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-widest shadow-sm ${badgeClass}`}>
                                {statusText}
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-xs font-bold text-[var(--color-text)] mb-2 uppercase tracking-wide">{label}</label>
      <div className="relative">
        <input type={show ? 'text' : 'password'} className="input pr-10 bg-[var(--color-bg)] border-[var(--glass-border)] focus:border-[var(--color-accent)] focus:ring-0 rounded-xl" value={value}
          onChange={e => onChange(e.target.value)} placeholder={placeholder ?? '••••••••'} />
        <button type="button" onClick={() => setShow(s => !s)}
          className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--color-muted)' }}>
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function Profile() {
  const { user, updateProfile, changePassword } = useAuth();
  const { toast } = useToast();

  const [profile, setProfile] = useState({ name: '', phone: '' });
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name ?? '', phone: user.phone ?? '' });
    }
  }, [user?.name, user?.phone]);

  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const handleProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile.name.trim()) { toast('Name is required.', 'error'); return; }
    setProfileLoading(true);
    const res = await updateProfile({ name: profile.name, phone: profile.phone });
    setProfileLoading(false);
    if (res.ok) toast('Profile updated successfully!', 'success');
    else toast(res.error || 'Update failed', 'error');
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw.current) { toast('Current password required.', 'error'); return; }
    if (pw.next.length < 8) { toast('New password must be at least 8 characters.', 'error'); return; }
    if (pw.next !== pw.confirm) { toast('New passwords do not match.', 'error'); return; }
    setPwLoading(true);
    const res = await changePassword(pw.current, pw.next);
    setPwLoading(false);
    if (res.ok) { toast('Password changed successfully!', 'success'); setPw({ current: '', next: '', confirm: '' }); }
    else toast(res.error || 'Password change failed', 'error');
  };

  return (
    <div className="p-6 md:p-8 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <h1 className="text-xl md:text-2xl font-black text-[var(--color-text)] tracking-tight">Account & Security</h1>

      <div className="bg-[var(--color-bg)] rounded-2xl border border-[var(--glass-border)] p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8 pb-8 border-b border-[var(--glass-border)]">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-black text-white shrink-0 shadow-lg shadow-[var(--color-accent)]/20" style={{ background: 'var(--color-accent)' }}>
            {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-black text-[var(--color-text)] text-2xl tracking-tight leading-tight">{user?.name || 'Investor Account'}</p>
            <p className="text-sm text-[var(--color-muted)] font-medium mt-1">{user?.email}</p>
            <div className="flex gap-2 mt-3">
              <span className="text-[10px] font-black tracking-widest bg-[rgba(95,156,76,0.1)] text-[var(--color-accent)] px-2 py-0.5 rounded-full uppercase">VERIFIED PARTNER</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleProfile} className="space-y-6 max-w-2xl" noValidate>
          <div className="flex items-center gap-2 mb-2">
            <Edit3 className="w-4 h-4 text-[var(--color-accent)]" />
            <h3 className="text-sm font-black text-[var(--color-text)] tracking-tight">Personal Details</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-bold text-[var(--color-text)] mb-2 uppercase tracking-wide">Full Name</label>
              <input className="input bg-[var(--color-bg)] border-[var(--glass-border)] focus:border-[var(--color-accent)] focus:ring-0 rounded-xl" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} placeholder="Your name" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[var(--color-text)] mb-2 uppercase tracking-wide">Phone Number</label>
              <input type="tel" className="input bg-[var(--color-bg)] border-[var(--glass-border)] focus:border-[var(--color-accent)] focus:ring-0 rounded-xl" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-[var(--color-text)] mb-2 uppercase tracking-wide">Email Address</label>
            <input className="input opacity-60 cursor-not-allowed bg-[var(--color-bg)] border-[var(--glass-border)] rounded-xl" value={user?.email ?? ''} readOnly />
          </div>
          <button type="submit" disabled={profileLoading} className="btn btn-primary px-8 py-3 rounded-xl text-[11px] font-black uppercase tracking-[0.15em] disabled:opacity-60 shadow-lg shadow-[var(--color-accent)]/20">
            {profileLoading ? 'Updating…' : 'Update Profile'}
          </button>
        </form>
      </div>

      <div className="bg-[var(--color-bg)] rounded-2xl border border-[var(--glass-border)] p-6 md:p-8">
        <div className="flex items-center gap-2 mb-8">
          <Lock className="w-4 h-4 text-[var(--color-accent)]" />
          <h3 className="text-sm font-black text-[var(--color-text)] tracking-tight">Security Credentials</h3>
        </div>
        <form onSubmit={handlePassword} className="space-y-6 max-w-lg" noValidate>
          <PwField label="Current Password" value={pw.current} onChange={v => setPw(p => ({ ...p, current: v }))} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <PwField label="New Password" value={pw.next} onChange={v => setPw(p => ({ ...p, next: v }))} placeholder="Min. 8 char" />
            <PwField label="Confirm New Password" value={pw.confirm} onChange={v => setPw(p => ({ ...p, confirm: v }))} placeholder="Repeat new" />
          </div>
          <button type="submit" disabled={pwLoading} className="btn btn-primary px-8 py-3 rounded-xl text-[11px] font-black uppercase tracking-[0.15em] disabled:opacity-60 shadow-lg shadow-[var(--color-accent)]/20">
            {pwLoading ? 'Generating…' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function UserDashboard() {
  const { user } = useAuth();
  const { triggerNavbarAutoHide } = useUI();
  const loc = useLocation();

  // Security: Disable right-click on the dashboard
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  if (!user) return <Navigate to="/login" />;

  const currentRoute = userNav.find(n => loc.pathname === n.to) || userNav[0];

  return (
    <div className="min-h-screen flex bg-[var(--color-bg)] font-sans selection:bg-[var(--color-accent)] selection:text-white">
      {/* Sidebar - Clean, integrated, full-height */}
      <aside className="hidden lg:flex flex-col w-[200px] bg-[#293064] border-r border-[var(--glass-border)] shrink-0 sticky top-0 h-screen">
        <div className="p-6 pb-8">
          <nav className="flex-1 space-y-1">
            {userNav.map(n => {
              const Icon = n.icon;
              const active = loc.pathname === n.to;
              return (
                <Link key={n.to} to={n.to}
                  onClick={triggerNavbarAutoHide}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition-all group ${active
                    ? 'bg-[rgba(95,156,76,0.1)] text-[var(--color-accent)] border border-[rgba(95,156,76,0.15)] shadow-sm'
                    : 'text-[var(--color-muted)] hover:bg-[var(--color-bg-2)] hover:text-[var(--color-text)]'
                    }`}>
                  <Icon className={`w-4 h-4 transition-transform group-hover:scale-110 ${active ? 'text-[var(--color-accent)]' : 'text-white group-hover:text-black'}`} />
                  <span className={`flex-1 ${active ? 'text-[var(--color-accent)]' : 'text-white group-hover:text-black'}`}>{n.label}</span>
                  {active && <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-accent)] shadow-[0_0_8px_rgba(95,156,76,0.5)]" />}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 min-w-0 flex flex-col h-screen overflow-y-auto bg-white">
        <main className="flex-1">
          {/* Mobile navigation (visible only on small screens) */}
          <div className="lg:hidden p-4 bg-[var(--color-bg)] border-b border-[var(--glass-border)]">
            <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
              {userNav.map(n => {
                const Icon = n.icon;
                const active = loc.pathname === n.to;
                return (
                  <Link key={n.to} to={n.to}
                    onClick={triggerNavbarAutoHide}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold whitespace-nowrap active:scale-95 transition-all ${active ? 'bg-[var(--color-accent)] text-white shadow-md' : 'bg-[var(--color-bg-2)] text-[var(--color-muted)]'
                      }`}>
                    <Icon className="w-3.5 h-3.5" />{n.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <Routes>
            <Route index element={<Overview />} />
            <Route path="my-videos" element={<MyVideos />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="profile" element={<Profile />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

